import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { MdSearch, MdOutlineCampaign, MdOutlineNotificationsActive, MdOutlineVerifiedUser } from "react-icons/md";
import { AppDispatch } from "../../store";
import { PricingSearchItem } from "../../types/pricingType";
import { fetchPricingSearch } from "../../features/pricing/PricingService";

type TemplateType = "Marketing" | "Utility" | "Authentication";

const TYPES: { key: TemplateType; text: string; icon: React.ReactNode; tone: string }[] = [
  { key: "Marketing", text: "Offers, announcements and re-engagement", icon: <MdOutlineCampaign size={20} />, tone: "bg-sky-50 text-sky-700" },
  { key: "Utility", text: "Order updates, reminders and alerts", icon: <MdOutlineNotificationsActive size={20} />, tone: "bg-emerald-50 text-emerald-700" },
  { key: "Authentication", text: "One-time passwords and logins", icon: <MdOutlineVerifiedUser size={20} />, tone: "bg-violet-50 text-violet-700" },
];

const PAGE = 20;

/** What each WhatsApp template message costs, by country (charged from the wallet). */
export default function PricingPerMsg() {
  const dispatch = useDispatch<AppDispatch>();
  const [templateType, setTemplateType] = useState<TemplateType>("Marketing");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [results, setResults] = useState<PricingSearchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // search after typing stops
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        setLoading(true);
        const result = await dispatch(
          fetchPricingSearch({ templateType, page, size: PAGE, ...(search ? { country: search } : {}) }),
        ).unwrap();
        if (!live) return;
        setResults(result.results || []);
        setTotal(result.page?.total ?? result.results?.length ?? 0);
      } catch (err) {
        console.error(err);
        if (live) setResults([]);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [templateType, page, search, dispatch]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Message rates</h1>
        <p className="mt-2 text-sm text-slate-500">
          Each template message is charged from your wallet by its type and the recipient's country.
        </p>
      </div>

      {/* Message types */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {TYPES.map((t) => {
          const on = templateType === t.key;
          return (
            <button
              key={t.key}
              onClick={() => {
                setTemplateType(t.key);
                setPage(0);
              }}
              aria-pressed={on}
              className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
                on ? "border-brand-main bg-white shadow-md ring-1 ring-brand-main" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <span className={`grid h-10 w-10 flex-none place-items-center rounded-xl ${t.tone}`}>{t.icon}</span>
              <span>
                <span className="block text-sm font-bold text-slate-900">{t.key}</span>
                <span className="block text-xs text-slate-500">{t.text}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <label className="relative block w-full sm:max-w-xs">
        <MdSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a country"
          aria-label="Search a country"
          className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand-main"
        />
      </label>

      {/* Rates */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Country</th>
                <th className="px-5 py-3 font-semibold">Dialling code</th>
                <th className="px-5 py-3 font-semibold text-right">Price per message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && results.length === 0 ? (
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    <td colSpan={3} className="px-5 py-3">
                      <div className="h-5 animate-pulse rounded bg-slate-100" />
                    </td>
                  </tr>
                ))
              ) : results.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-12 text-center text-slate-400">
                    {search ? `No rates for “${search}”.` : "No rates set for this message type yet."}
                  </td>
                </tr>
              ) : (
                results.map((item) => (
                  <tr key={item.id} className="transition hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <span className="font-medium text-slate-800">{item.country}</span>
                      <span className="ml-2 text-xs text-slate-400">{item.countryCode}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-600">+{item.phoneCode}</td>
                    <td className="px-5 py-3 text-right font-semibold text-slate-900">
                      {item.currency} {item.sellingPrice.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm">
            <span className="text-slate-500">
              Page {page + 1} of {pages}
            </span>
            <div className="flex gap-2">
              <button
                className="rounded-full border border-slate-200 px-3 py-1 font-semibold disabled:opacity-40"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <button
                className="rounded-full border border-slate-200 px-3 py-1 font-semibold disabled:opacity-40"
                disabled={page + 1 >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <p className="text-center text-xs text-slate-400">Prices exclude GST.</p>
    </div>
  );
}
